import { api, type Me } from '@/api'
import { aiAccountStatus, readAiAccountStatus } from '@/lib/aiAccountGuard'
import { mustPickEnterprise } from '@/lib/enterprisePicker'
import { onboardingSession } from '@/lib/onboardingSession'
import { setOnboardingState } from '@/router'

/**
 * Whether this page load follows the AI account status: only for the owner, once an enterprise is picked. Anybody
 * else is read nothing, shown no banner, and never landed; an action they take still asks the guard, which reads then.
 */
let following = false

/**
 * #1796: what a page load learns about the AI account, and the new user's one landing on the account step.
 *
 * - The status is read and recorded (`readAiAccountStatus`), for the shell's banner and the action guard. Nothing is
 *   held on it: every page opens without an account.
 * - An owner with no account is landed on the step once, straight after sign-up: only when the server's per-person
 *   record (#1718) says they have never reached it. Once they have, deep links and reloads open where they were asked
 *   to. The record is read quietly, so it logs no resume and marks no step shown.
 * - Nothing is guessed. A status or a record that cannot be read lands nobody.
 */
export async function loadOnboardingGate(person: Me | null): Promise<void> {
    following = person?.isOwner === true && !mustPickEnterprise(person)
    if (!following) {
        return
    }
    if ((await readAiAccountStatus()) !== 'not_connected') {
        return
    }
    if ((await aiAccountStepReached()) === false) {
        setOnboardingState({ inOnboarding: true, accountConnected: false })
    }
}

/**
 * The status is read again after every navigation (App.vue) while it is not `connected`, so an account connected in
 * another tab clears the banner at the next move. Once it reads `connected` it asks nothing more. It never holds or
 * lands anybody.
 */
export async function recheckOnboardingGate(): Promise<void> {
    if (!following || aiAccountStatus() === 'connected') {
        return
    }
    await readAiAccountStatus()
}

/** Whether the person reading has ever reached the AI account step, or null when that could not be read. */
async function aiAccountStepReached(): Promise<boolean | null> {
    try {
        const state = await api.onboarding('guided', onboardingSession(), { quiet: true })
        return typeof state.aiAccountStepReached === 'boolean' ? state.aiAccountStepReached : null
    } catch {
        return null
    }
}
